
export enum TableShape {
  RECTANGLE = 'RECTANGLE',
  CIRCLE = 'CIRCLE',
  TRIANGLE = 'TRIANGLE'
}

export interface CategoryDefinition {
  id: string;
  tenantId?: string;
  name: string;
  color: string;
  borderColor: string;
  icon: string; 
  minDuration: number; 
  defaultCapacity?: number; 
}


export enum UserRole {
  GUEST = 'GUEST',
  ADMIN = 'ADMIN'
}

export interface User {
  id: string;
  tenantId?: string;
  name: string;
  email: string;
  role: UserRole;
}

export enum ZoneType {
  SPORT_AREA = 'SPORT_AREA',   
  LOCKER_ROOM = 'LOCKER_ROOM', 
  LOUNGE = 'LOUNGE',         
  ENTRANCE = 'ENTRANCE',
  OBSTACLE = 'OBSTACLE', 
  CUSTOM = 'CUSTOM'
}

export interface Zone {
  id: string;
  tenantId?: string;
  name: string;
  type: ZoneType;
  x: number;
  y: number;
  width: number;
  height: number;
  customColor?: string;
}

export interface Table {
  id: string;
  tenantId?: string;
  name: string;
  capacity: number; 
  shape: TableShape;
  category: string; 
  x: number;
  y: number;
  width?: number;
  height?: number;
  rotation: number;
}

export interface Reservation {
  id: string;
  tenantId?: string;
  tableId: string;
  categoryId: string;
  packageName?: string;
  userId?: string;
  customerName: string;
  phone?: string;
  email?: string;
  partySize: number; 
  date: string;
  time: string;
  durationMinutes: number;
  notes?: string;
  status: 'CONFIRMED' | 'PENDING' | 'CANCELLED_BY_GUEST';
  seriesId?: string; 
}

export interface TimeRange {
  open: string;
  close: string;
  isClosed: boolean;
}

export type WeekSchedule = Record<string, TimeRange>;

export type GuestViewMode = 'MAP' | 'SIMPLE';
export type ReservationMode = 'GENERAL' | 'SPECIFIC_CATEGORY';
export type DesignMode = 'MODERN' | 'SIMPLE';
export type LayoutTemplate = 'DEMO' | 'STANDARD' | 'LIGHT' | 'SUSPENDED';

export interface ArenaTheme {
  mode: DesignMode;
  darkMode: boolean;
  primaryColor: string;
  secondaryColor: string;
  calendarConfirmedColor?: string;
  calendarPendingColor?: string;
}

export interface PackageBooking {
  id: string;
  tenantId?: string;
  name: string;
  icon: string;
  categoryId: string;
  allowedDays: string[];
  startTime: string;
  endTime: string;
  defaultPartySize: number;
  durationMinutes: number;
  extendedInfo?: string; 
}


export interface SeoConfig {
  pageTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  ogLocale?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonicalUrl?: string;
  faviconUrl?: string;
  themeColor?: string;
  robotsDirective?: string;
  structuredData?: string;
  customHeadTags?: string;
}

export interface ArenaConfig {
  tenantId: string;
  layoutTemplate: LayoutTemplate;
  name: string;
  address?: string;
  phoneLandline?: string;
  phoneMobile?: string;
  contactEmail?: string;
  websiteUrl?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  twitterUrl?: string;
  tiktokUrl?: string;
  ownerName?: string;
  showOwnerOnStart: boolean;
  logoUrl?: string;
  welcomeText?: string;
  schedule: WeekSchedule;
  guestViewMode: GuestViewMode;
  reservationMode: ReservationMode;
  lastReservationBuffer: number;
  allowGuestReservations: boolean;
  requireReservationApproval: boolean;
  allowRecurringBookings: boolean; 
  recurringRequireApproval: boolean; 
  theme: ArenaTheme;
  socialProviders: string[];
  packages: PackageBooking[];
  seo?: SeoConfig;
}

export interface FloorDimensions {
  width: number;
  height: number;
}

export interface DragState {
  isDragging: boolean;
  type: 'TABLE' | 'ZONE' | 'RESIZE_ZONE' | 'RESIZE_TABLE' | null;
  itemId: string | null;
  offsetX: number;
  offsetY: number;
  startWidth?: number;
  startHeight?: number;
  startX?: number;
  startY?: number;
}